import React from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, Search, Edit2 } from "lucide-react";
import { APPOINTMENT_STATUS_RO } from "../../lib/statusLabels";
import { useAuth } from "../../contexts/AuthContext";
import CityCombobox from "../../components/CityCombobox";

const empty = () => ({ service_type: "Instalare", name: "", phone: "", email: "",
    address: "", city: "", county: "", date: "", time_slot: "", notes: "",
    installer_id: "", status: "pending", source: "admin" });

export default function AdminAppointments() {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "superadmin";
    const [items, setItems] = React.useState([]);
    const [installers, setInstallers] = React.useState([]);
    const [q, setQ] = React.useState("");
    const [dateFrom, setDateFrom] = React.useState("");
    const [dateTo, setDateTo] = React.useState("");
    const [editing, setEditing] = React.useState(null);
    const [counties, setCounties] = React.useState([]);
    const [cities, setCities] = React.useState([]);

    React.useEffect(() => {
        api.get("/locations/counties").then(r => setCounties(r.data || [])).catch(() => {});
    }, []);
    React.useEffect(() => {
        const county = editing?.county;
        if (county) api.get("/locations/cities", { params: { county } }).then(r => setCities(r.data || [])).catch(() => {});
        else setCities([]);
    }, [editing?.county]);

    const load = React.useCallback(async () => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (dateFrom) params.set("date_from", dateFrom);
        if (dateTo) params.set("date_to", dateTo);
        const r = await api.get("/admin/appointments/search?" + params.toString());
        setItems(r.data || []);
    }, [q, dateFrom, dateTo]);

    React.useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
    }, [load]);

    React.useEffect(() => {
        api.get("/admin/users").then(r => setInstallers((r.data || []).filter(u =>
            u.role === "installer" || (u.roles || []).includes("installer"))));
    }, []);

    const save = async () => {
        try {
            const payload = { ...editing, installer_id: editing.installer_id || null };
            if (editing.appointment_id) await api.put(`/admin/appointments/${editing.appointment_id}`, payload);
            else await api.post("/admin/appointments", payload);
            toast.success("Salvat"); setEditing(null); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
    };
    const del = async (id) => {
        if (!window.confirm("Ștergi programarea?")) return;
        await api.delete(`/admin/appointments/${id}`); toast.success("Șters"); load();
    };
    const quickStatus = async (a, status) => {
        await api.put(`/admin/appointments/${a.appointment_id}`, { status });
        load();
    };

    return (
        <div data-testid="admin-appointments">
            <div className="flex items-start justify-between mb-6">
                <div><div className="overline mb-1">Administrare</div><h1 className="font-heading text-3xl">Programări</h1></div>
                <button onClick={() => setEditing(empty())} className="btn-primary flex items-center gap-2" data-testid="appt-create"><Plus className="h-4 w-4" /> Programare nouă</button>
            </div>

            <div className="card-soft p-3 mb-4 flex flex-wrap gap-3 items-center">
                <div className="flex items-center gap-2 flex-1 min-w-[240px]">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută după nume, telefon, adresă..." className="input-soft !py-2 flex-1" data-testid="appt-search" />
                </div>
                <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Data:</span>
                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="input-soft !py-1 !text-xs" data-testid="appt-date-from" />
                    <span className="text-muted-foreground">→</span>
                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="input-soft !py-1 !text-xs" data-testid="appt-date-to" />
                </div>
                <span className="text-xs text-muted-foreground">{items.length}</span>
            </div>

            <div className="card-soft overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted"><tr>{["Data/Ora", "Client", "Serviciu", "Adresă", "Instalator", "Status", ""].map(h => <th key={h} className="text-left px-3 py-3 text-xs uppercase text-muted-foreground">{h}</th>)}</tr></thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-muted-foreground">Nicio programare.</td></tr>}
                        {items.map((a, i) => (
                            <tr key={a.appointment_id} className="border-t border-border hover:bg-muted/30" data-testid={`appt-row-${i}`}>
                                <td className="px-3 py-2 text-xs font-mono">{a.date || "—"} {a.time_slot}</td>
                                <td className="px-3 py-2"><div className="font-medium">{a.name}</div><div className="text-xs text-muted-foreground">{a.phone}</div></td>
                                <td className="px-3 py-2 text-xs">{a.service_type}</td>
                                <td className="px-3 py-2 text-xs">{a.address}, {a.city}</td>
                                <td className="px-3 py-2">
                                    <select value={a.installer_id || ""} onChange={(e) => quickStatus(a, a.status) || api.put(`/admin/appointments/${a.appointment_id}`, { installer_id: e.target.value || null }).then(load)}
                                            className="input-soft !py-1 text-xs" data-testid={`appt-installer-${i}`}>
                                        <option value="">—</option>
                                        {installers.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                                    </select>
                                </td>
                                <td className="px-3 py-2">
                                    <select value={a.status} onChange={(e) => quickStatus(a, e.target.value)} className="input-soft !py-1 text-xs">
                                        {Object.entries(APPOINTMENT_STATUS_RO).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                                    </select>
                                </td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                    <button onClick={() => setEditing(JSON.parse(JSON.stringify(a)))} className="btn-ghost"><Edit2 className="h-4 w-4" /></button>
                                    {isSuperAdmin && <button onClick={() => del(a.appointment_id)} className="btn-ghost text-destructive" title="Șterge (superadmin)"><Trash2 className="h-4 w-4" /></button>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="p-5 border-b border-border flex items-center justify-between">
                            <h3 className="font-heading text-xl">{editing.appointment_id ? "Editează programare" : "Programare nouă"}</h3>
                            <button onClick={() => setEditing(null)} className="btn-ghost"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <input value={editing.service_type || ""} onChange={(e) => setEditing({ ...editing, service_type: e.target.value })} placeholder="Tip serviciu" className="input-soft" data-testid="appt-service" />
                                <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="input-soft">
                                    {Object.entries(APPOINTMENT_STATUS_RO).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                                </select>
                                <input required value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Nume client *" className="input-soft" data-testid="appt-name" />
                                <input value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} placeholder="Telefon" className="input-soft" data-testid="appt-phone" />
                                <input value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} placeholder="Email (opțional)" className="input-soft md:col-span-2" />
                                <input value={editing.address || ""} onChange={(e) => setEditing({ ...editing, address: e.target.value })} placeholder="Adresă" className="input-soft md:col-span-2" />
                                <select value={editing.county || ""} onChange={(e) => setEditing({ ...editing, county: e.target.value, city: "" })} className="input-soft">
                                    <option value="">Județ</option>
                                    {counties.map(c => <option key={c}>{c}</option>)}
                                </select>
                                <CityCombobox
                                    cities={cities}
                                    value={editing.city || ""}
                                    onChange={(v) => setEditing({ ...editing, city: v })}
                                    placeholder="Localitate"
                                />
                                <input type="date" value={editing.date || ""} onChange={(e) => setEditing({ ...editing, date: e.target.value })} className="input-soft" data-testid="appt-date" />
                                <input value={editing.time_slot || ""} onChange={(e) => setEditing({ ...editing, time_slot: e.target.value })} placeholder="Interval orar (ex 10:00-14:00)" className="input-soft" />
                                <select value={editing.installer_id || ""} onChange={(e) => setEditing({ ...editing, installer_id: e.target.value })} className="input-soft md:col-span-2">
                                    <option value="">Fără instalator atribuit</option>
                                    {installers.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                                </select>
                            </div>
                            <textarea rows="3" value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} placeholder="Observații" className="input-soft" />
                        </div>
                        <div className="p-5 border-t border-border flex justify-end gap-2">
                            <button onClick={() => setEditing(null)} className="btn-outline">Anulează</button>
                            <button onClick={save} className="btn-primary" data-testid="appt-save">Salvează</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
